import type { PermissionCheckInput } from '@rematter/frontend-lib-api-client/graphql/schema';
import { useQuery } from '@tanstack/react-query';

import { useApi } from './useApi';

import type { ApiContextValue } from '~/shared/providers/api/ApiContext';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const PERMISSION_QUERY_KEY = 'core.checkPermission';
export const PERMISSION_STALE_TIME = 5 * 60 * 1000;
export const BATCH_PERMISSION_QUERY_KEY = `${PERMISSION_QUERY_KEY}.batch`;

type PermissionCheckResult = { action: string; allowed: boolean };

export interface PermissionScope {
  resource: string;
  resourceId: string;
}

export function createBatchPermissionsQueryOptions(
  clientFn: ApiContextValue['client'],
  actions: string[],
  scope?: PermissionScope,
) {
  const sortedActions = [...new Set(actions)].sort();
  return {
    queryKey: [BATCH_PERMISSION_QUERY_KEY, sortedActions, scope] as const,
    queryFn: async () => {
      const inputs = sortedActions.map((action) => ({
        action,
        ...(scope
          ? { resource: scope.resource, resourceId: scope.resourceId }
          : {}),
      }));

      const result = await clientFn(`query.${PERMISSION_QUERY_KEY}`).query({
        checkUserPermissions: {
          __args: { inputs },
          allowed: true,
          action: true,
        },
        __name: 'coreCheckPermissions',
      });

      return new Map<string, boolean>(
        result.checkUserPermissions.map((p: PermissionCheckResult) => [
          p.action,
          p.allowed,
        ]),
      );
    },
    staleTime: PERMISSION_STALE_TIME,
    enabled: actions.length > 0,
  };
}

export function createPermissionQueryOptions(
  clientFn: ApiContextValue['client'],
  action: string,
  scope?: PermissionScope,
) {
  const input: PermissionCheckInput = { action };
  if (scope) {
    input.resource = scope.resource;
    input.resourceId = scope.resourceId;
  }

  return {
    queryKey: [PERMISSION_QUERY_KEY, { action, ...scope }] as const,
    queryFn: async () => {
      const result = await clientFn(`query.${PERMISSION_QUERY_KEY}`).query({
        checkUserPermission: {
          __args: { input },
          allowed: true,
        },
        __name: 'coreCheckPermission',
      });
      return result.checkUserPermission.allowed;
    },
    staleTime: PERMISSION_STALE_TIME,
    enabled: action.length > 0,
  };
}

export function createPermissionAtom(action: string) {
  return createQueryAtomFactory(
    `${PERMISSION_QUERY_KEY}.${action}` as `${string}.${string}`,
    () => ({
      checkUserPermission: {
        __args: { input: { action } },
        allowed: true,
      },
    }),
    {
      select: (data) => data?.checkUserPermission?.allowed ?? false,
      staleTime: PERMISSION_STALE_TIME,
    },
  );
}

export function usePermission(
  action: string,
  scope?: PermissionScope,
): {
  allowed: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const { client } = useApi();

  const { data, isLoading, isError, error } = useQuery(
    createPermissionQueryOptions(client, action, scope),
  );

  return {
    allowed: data ?? false,
    isLoading,
    isError,
    error,
  };
}
